import {AEM_REDIRECT_VIEW_IDS} from '@/consts/aem/aemRedirectViewsIds';
import {APP_SECTION_TYPES, EXTERNAL_LINK_TYPES, externalLinkTypes} from '@/consts/externalLinkTypes';
import {resolveUseConfigExternalLinkBatch, resolveUseConfigExternalLinkFlags} from '@/hooks/sso/ssoLinkMode';
import useSsoLinkBatch from '@/hooks/sso/useSsoLinkBatch';
import useIsDcxMarket from '@/hooks/useIsDcxMarket';
import domService from '@/services/domService';
import log from '@/services/logger/log';
import appRouterService from '@/services/route/appRouterService';
import {showFirmwareAvailableModalAction, showLeviaLearnMoreModalAction} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';
import type {ExternalLinkModel} from '@/types/aem/externalLinkModel';
import {getHwHeight} from '@/views/WarningBanners/hwUtils';

import useExternalLink from '../useExternalLink';
import usePageSectionLinkData from './usePageSectionLinkData';

export type UseConfigExternalLinkProps = Partial<ExternalLinkModel> & {
    disabled?: boolean;
    source?: string;
    ssoParams?: Record<string, unknown>;
};

export type UseConfigExternalLinkResult = {
    redirectToExternalLink: (() => void | Promise<void>) | undefined;
    externalLink: string | undefined;
};

type PageSectionLinkArgs = {
    redirectTo?: ExternalLinkModel['redirectTo'];
    scrollTo?: ExternalLinkModel['scrollTo'];
    isMobile: boolean;
    redirectToAutoDelivery: () => void | Promise<void>;
};

const useConfigExternalLink = (props?: UseConfigExternalLinkProps | null): UseConfigExternalLinkResult => {
    const {
        disabled,
        externalLinkCode,
        ssoTemplate,
        linkType,
        source,
        redirectTo,
        scrollTo,
        ssoParams,
        ssoUrl,
        queryStringTemplate,
    } = props || {};
    const pageSectionLinkData = usePageSectionLinkData();
    const {isSso, isExternalSsoUrl} = resolveUseConfigExternalLinkFlags(linkType, ssoUrl);
    const isDcx = useIsDcxMarket();
    const {useBatchForSso, useBatch} = resolveUseConfigExternalLinkBatch({
        isDcx,
        isSso,
        isExternalSsoUrl,
    });

    const {redirect: redirectFromExternalLink, externalLink} = useExternalLink({
        disabled: disabled || useBatch,
        url: isSso ? undefined : ssoUrl,
        externalLinkCode: isSso ? undefined : externalLinkCode,
        ssoTemplate: isSso ? ssoTemplate : undefined,
        ssoParams,
        queryStringTemplate,
        openInBluefy: externalLinkTypes.isUrlInApp(linkType) || externalLinkTypes.isSsoLinkInApp(linkType),
        linkProcessor: undefined,
        deviceType: undefined,
        deviceSerialNumber: undefined,
    });
    const {redirect: batchRedirect, link: batchLink} = useSsoLinkBatch({
        ssoTemplate: useBatchForSso ? ssoTemplate : undefined,
        url: isExternalSsoUrl ? ssoUrl : undefined,
        params: ssoParams,
        disabled: disabled || !useBatch,
        queryStringTemplate,
        openInBluefy: useBatchForSso && externalLinkTypes.isSsoLinkInApp(linkType),
        linkProcessor: undefined,
        deviceType: undefined,
        deviceSerialNumber: undefined,
    });
    const redirectHandler = useBatch ? batchRedirect : redirectFromExternalLink;
    const resolvedExternalLink = useBatch ? batchLink : externalLink;

    switch (linkType?.toLowerCase()) {
        case EXTERNAL_LINK_TYPES.SECTION_LINK:
            return {
                redirectToExternalLink: handleSectionLink({sectionLink: ssoUrl, source}),
                externalLink: undefined,
            };
        case EXTERNAL_LINK_TYPES.PAGE_SECTION:
            return {
                redirectToExternalLink: handlePageSectionLink({
                    redirectTo,
                    scrollTo,
                    isMobile: pageSectionLinkData.isMobile,
                    redirectToAutoDelivery: pageSectionLinkData.redirectToAutoDelivery,
                }),
                externalLink: undefined,
            };
        case EXTERNAL_LINK_TYPES.URL:
        case EXTERNAL_LINK_TYPES.URL_IN_APP:
        case EXTERNAL_LINK_TYPES.SSO_URL:
        case EXTERNAL_LINK_TYPES.SSO_LINK:
        case EXTERNAL_LINK_TYPES.SSO_LINK_IN_APP:
            return {
                redirectToExternalLink: redirectHandler,
                externalLink: resolvedExternalLink,
            };
        default:
            return {redirectToExternalLink: undefined, externalLink: undefined};
    }
};

const handleSectionLink = ({sectionLink, source}: {sectionLink?: string; source?: string}) => {
    switch (sectionLink?.toLowerCase()) {
        case APP_SECTION_TYPES.FIRMWARE_AVAILABLE:
            return () => dispatch!(showFirmwareAvailableModalAction({source}));
        case APP_SECTION_TYPES.LEVIA:
            return () => dispatch!(showLeviaLearnMoreModalAction({source}));
        case APP_SECTION_TYPES.SHOP:
            return () => appRouterService.forwardToShopPage();
        default:
            return () => {
                log.info(`useConfigExternalLink, sectionLink is empty or not supported, sectionLink: ${sectionLink}`);
            };
    }
};

const handlePageSectionLink = ({redirectTo, scrollTo, isMobile, redirectToAutoDelivery}: PageSectionLinkArgs) => {
    const {id: redirectToId} = redirectTo || {};
    const {id: scrollToId} = scrollTo || {};

    return () => {
        if (redirectToId) {
            redirectToSection({id: redirectToId, redirectToAutoDelivery});
        } else if (scrollToId) {
            scrollToElement({id: scrollToId, isMobile});
        }
    };
};

const redirectToSection = ({
    id,
    redirectToAutoDelivery,
}: {
    id: string;
    redirectToAutoDelivery: () => void | Promise<void>;
}) => {
    switch (id) {
        case AEM_REDIRECT_VIEW_IDS.SHOP:
            appRouterService.forwardToShopPage();
            break;
        case AEM_REDIRECT_VIEW_IDS.DEVICES:
            appRouterService.forwardToDevicesPage();
            break;
        case AEM_REDIRECT_VIEW_IDS.SELECT_YOUR_DEVICE:
            appRouterService.forwardToSelectYourDevicePage();
            break;
        case AEM_REDIRECT_VIEW_IDS.AUTO_DELIVERY_DETAILS:
            redirectToAutoDelivery();
            break;
        default:
            return;
    }
};

const scrollToElement = ({id, isMobile}: {id: string; isMobile: boolean}) => {
    const stickyHeaderHeight = domService.getStickyHeaderHeight();
    const hwHeight = getHwHeight();
    const offsetTop = isMobile ? -(stickyHeaderHeight + hwHeight) : 0;

    const element = document.querySelector(`#${id}`);

    if (element) {
        const y = window.scrollY + element.getBoundingClientRect().top + offsetTop;

        window.scrollTo({top: y, behavior: 'smooth'});
    }
};

export default useConfigExternalLink;
